import { Button, Form, FormGroup, Modal, TextInput } from '@ksyos/design-system';
import { Koi } from '@ksyos/koi';
import { Fragment, useState } from 'react';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils';
import type { ISchemaContainer } from '../../../../../common/validation/schemaContainer';
import type { ValidationErrors } from '../../../../utils/validation';
import { emptyValidationErrors, validate } from '../../../../utils/validation';

interface IProvideReasonForAccessModalProps {
    onCloseModal: () => void;
    onDownload: () => Promise<void>;
    sensitiveDataColumns: string[];
}

interface IAccessReasonValidated {
    accessReason: string;
}

export function ProvideReasonForAccessModal(props: IProvideReasonForAccessModalProps) {
    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [accessReason, setAccessReason] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const accessReasonValidation = Koi.object().keys({
        accessReason: Koi.string().min(5).max(1000),
    });

    const accessReasonSchema: ISchemaContainer<IAccessReasonValidated> = {
        schema: accessReasonValidation.options({ convert: false, presence: 'required' }),
    };

    return (
        <Modal
            isOpen
            onClose={props.onCloseModal}
            size="large"
        >
            <Fragment>
                <Modal.Header>{language.finance.reasonForAccessModel.title}</Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <p>{language.finance.reasonForAccessModel.subTitle}</p>
                    <Form submitOnEnter={false}>
                        <FormGroup
                            hasErrors={validationErrors.has('accessReason')}
                            isRequired={true}
                            label={language.finance.reasonForAccessModel.label}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(validationErrors.get('accessReason'))}
                                onChange={handleChange}
                                placeholder={language.finance.reasonForAccessModel.placeholder}
                                sx={{ width: '95%' }}
                                value={accessReason || ''}
                            />
                        </FormGroup>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={props.onCloseModal}
                        variant="outline"
                    >
                        {language.common.cancel}
                    </Button>
                    <Button onClick={async () => await onDownloadButtonClick()}>{language.common.download}</Button>
                </Modal.Footer>
            </Fragment>
        </Modal>
    );

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        setAccessReason(event.target.value);
        validateAccessReason(event.target.value);
    }

    function validateAccessReason(reason: string | null) {
        const result = validate(accessReasonSchema, { accessReason: reason });
        setValidationErrors(result.errors);
    }

    async function onDownloadButtonClick() {
        const { errors } = validate(accessReasonSchema, { accessReason });
        if (errors.size > 0) {
            setValidationErrors(errors);

            return;
        }

        await abortableFetch.fetchJson('/api/requests/sendFinancialExportSensitiveDataAccessedEmail', 'POST', {
            accessReason,
            sensitiveDataColumns: props.sensitiveDataColumns,
        });

        await props.onDownload();
    }
}
